import { lazy, type ComponentType } from "react";

// A lazy chunk that fails to load after a deploy is almost always a stale
// index: the browser (or the Capacitor WebView) still holds the old entry,
// whose chunk hashes no longer exist on the server. Retrying the import does
// not help — browsers cache a failed module import for the URL — so the fix
// is one full reload, which fetches the new index.
//
// The timestamp guard stops a reload loop when the chunk is genuinely
// unreachable (offline, outage): within the window the error is rethrown to
// the error boundary instead.
const RELOAD_KEY = "ubik:chunk-reload-at";
const RELOAD_WINDOW_MS = 30_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  return error instanceof Error && CHUNK_ERROR.test(error.message);
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    return true; // no sessionStorage → cannot guard, so never auto-reload
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches React.lazy's own signature
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isChunkLoadError(error) || reloadedRecently()) throw error;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      // Keep Suspense on its fallback until the reload takes over.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
